// Daily-driver shell: sidebar nav + topbar with search/theme/user.
// Used for the Projects / Repos / Artifacts / Build-Info screens
// where DevOps engineers spend their day.

import { NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderTree,
  Box,
  Activity,
  HelpCircle,
  Lock,
  Shield,
  ShieldAlert,
  ScanLine,
  Key,
  ArrowUpFromDot,
  Bot,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Wordmark } from './Logo';
import { PageContainer } from './PageContainer';
import { ThemeToggle } from './ui/ThemeToggle';
import { SearchBar } from './SearchBar';
import { UserMenu } from './UserMenu';
import { cn } from '../lib/cn';
import { useWhoami } from '../api/client';
import { useMaintenance } from '../api/maintenance';
import { usePublicConfig } from '../api/publicConfig';
import { MaintenanceBanner } from './MaintenanceBanner';
import { SystemBanner } from './SystemBanner';
import { SeedBanner } from './SeedBanner';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/projects', label: 'Projects', Icon: FolderTree },
  { to: '/repos', label: 'Repositories', Icon: Box },
  { to: '/builds', label: 'Build-Info', Icon: Activity },
  { to: '/tokens', label: 'API tokens', Icon: Key },
  { to: '/promotions', label: 'Promotions', Icon: ArrowUpFromDot },
  { to: '/service-accounts', label: 'Service accounts', Icon: Bot },
  { to: '/trash', label: 'Trash', Icon: Trash2 },
];

export function AppShell({ children }: Readonly<{ children: ReactNode }>) {
  const { user } = useWhoami();
  const { data: maint } = useMaintenance();
  const { data: pubConfig } = usePublicConfig();

  return (
    <>
      <div className="starfield-bg" aria-hidden="true" />
      <div className="min-h-screen flex">
        {/* Sidebar — sticky + self-scrolling so the Governance block stays
            reachable no matter how long the main content (e.g. a big repo
            list) gets. */}
        <aside className="w-60 shrink-0 border-r border-border bg-bg-elevated/40 backdrop-blur-sm flex flex-col sticky top-0 h-screen overflow-y-auto">
          <Link
            to="/"
            className="flex items-center px-5 h-16 border-b border-border hover:bg-bg-hover/50"
          >
            <Wordmark />
          </Link>

          <nav className="flex-1 p-3 space-y-0.5">
            <p className="orb-nav-label text-fg-subtle px-3 pt-3 pb-2">Workspace</p>
            {navItems.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {(user?.roles?.includes('org_admin') || user?.roles?.includes('admin')) && (
            <div className="p-3 border-t border-border space-y-0.5">
              <p className="orb-nav-label text-fg-subtle px-3 pb-2">Governance</p>
              <NavLink
                to="/detection/findings"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <ScanLine className="h-4 w-4 shrink-0" />
                <span>Detection</span>
              </NavLink>
              <NavLink
                to="/security"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-danger/10 text-danger border border-danger/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>Security</span>
              </NavLink>
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-nebula/10 text-nebula border border-nebula/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <Shield className="h-4 w-4 shrink-0" />
                <span>Admin</span>
              </NavLink>
            </div>
          )}
        </aside>

        {/* Main column */}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="relative z-40 h-16 border-b border-border bg-bg-base/80 backdrop-blur-sm flex items-center justify-between gap-6 px-6 shrink-0">
            <div className="flex-1 min-w-0 flex items-center gap-4">
              <span className="orb-eyebrow text-fg-subtle shrink-0 hidden xl:inline">
                self-hosted
              </span>
              <SearchBar />
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {pubConfig?.air_gapped_mode && (
                <a
                  href="/admin/network"
                  className="inline-flex items-center gap-1.5 px-2 h-7 rounded-md text-xs font-medium bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 transition-colors"
                  title="This deployment is in air-gapped-by-default mode. Outbound network calls are denied unless an admin opted in. Click to manage."
                >
                  <Lock className="h-3 w-3" />
                  <span>Air-gapped</span>
                </a>
              )}
              {pubConfig?.help_url && (
                <a
                  href={pubConfig.help_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
                  title="Help & documentation"
                  aria-label="Help"
                >
                  <HelpCircle className="h-4 w-4" />
                </a>
              )}
              <ThemeToggle />
              <UserMenu />
            </div>
          </header>

          {maint?.active && <MaintenanceBanner reason={maint.reason} until={maint.until} />}
          {pubConfig?.active_banner && <SystemBanner banner={pubConfig.active_banner} />}
          {pubConfig?.seed_active && <SeedBanner setName={pubConfig.seed_set} />}

          <main className="flex-1 py-6">
            <PageContainer>{children}</PageContainer>
          </main>
        </div>
      </div>
    </>
  );
}
